import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Calendar,
  Users,
  Activity,
  CreditCard,
  HelpCircle,
  Menu,
  Plus,
  DollarSign,
  UserPlus,
  QrCode,
  Package,
  Clock,
} from "lucide-react";

import { ROUTES } from "../../constants/routes";

/**
 * Navigation item definition for SidebarV2.
 *
 * Each item maps to one or more routes. `matchPaths` is used to determine
 * the active state -- if the current pathname starts with any of these
 * prefixes, the item is highlighted.
 */
interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  /** Additional path prefixes that should mark this item as active */
  matchPaths?: string[];
  /** When true, item is shown but not clickable */
  disabled?: boolean;
}

const navigationItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: Home,
    path: ROUTES.DASHBOARD,
  },
  {
    id: "students",
    label: "Students",
    icon: Users,
    path: ROUTES.STUDENTS,
    matchPaths: ["/students", "/upload-student-data"],
  },
  {
    id: "pulse",
    label: "RollOps Pulse",
    icon: Activity,
    path: ROUTES.ACTIVITY,
  },
  {
    id: "billing",
    label: "Billing & Subscriptions",
    icon: CreditCard,
    path: ROUTES.BILLING_UNIFIED,
    matchPaths: ["/billing", "/subscriptions-plans", "/discount-codes"],
  },
  {
    id: "inventory",
    label: "Inventory & Sales",
    icon: Package,
    path: ROUTES.INVENTORY_DASHBOARD,
  },
  {
    id: "schedule",
    label: "Schedule",
    icon: Calendar,
    path: ROUTES.SCHEDULE,
  },
  {
    id: "time-card",
    label: "Time Card",
    icon: Clock,
    path: ROUTES.TIME_CARD,
  },
];

// Shortened labels for the mobile bottom nav
const mobileLabels: Record<string, string> = {
  dashboard: "Home",
  schedule: "Schedule",
  students: "Students",
  pulse: "Pulse",
  billing: "Billing",
  inventory: "Inventory",
};

/**
 * Determine whether a nav item is active based on the current location.
 */
function isItemActive(item: NavItem, pathname: string): boolean {
  // Exact match on the primary path
  if (pathname === item.path) return true;

  // Dashboard also matches root "/"
  if (item.id === "dashboard" && pathname === "/") return true;

  // Check prefix matches
  const prefixes = item.matchPaths ?? [item.path];
  return prefixes.some(
    (prefix) => pathname.startsWith(prefix + "/") || pathname === prefix,
  );
}

interface SidebarV2Props {
  /** Controlled open state - when true, sidebar expands to show labels */
  isOpen?: boolean;
  /** Callback to toggle sidebar */
  onToggle?: () => void;
  /** Callback when + Payment hot button is clicked */
  onAddPayment?: () => void;
  /** Callback when + Student hot button is clicked */
  onAddStudent?: () => void;
  /** Callback when + QR hot button is clicked */
  onAddQR?: () => void;
}

/**
 * SidebarV2 - New sidebar following the prototype design.
 *
 * Desktop (md+): Full-height icon rail (w-20) that expands to w-64 when open.
 * Overlays page and header when expanded. Toggle is inside the sidebar.
 * Mobile (<md): Hidden; replaced with a fixed bottom navigation bar.
 *
 * Uses react-router <Link> for navigation and useLocation for active state.
 */
export function SidebarV2({
  isOpen = false,
  onToggle,
  onAddPayment,
  onAddStudent,
  onAddQR,
}: SidebarV2Props = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [hotButtonsOpen, setHotButtonsOpen] = useState(false);

  const isExpanded = isOpen;

  useEffect(() => {
    if (isExpanded) setHotButtonsOpen(false);
  }, [isExpanded]);

  const closeHotButtons = () => setHotButtonsOpen(false);

  const handleAddPayment = () => {
    closeHotButtons();
    if (onAddPayment) onAddPayment();
    else navigate(ROUTES.BILLING_UNIFIED);
  };
  const handleAddStudent = () => {
    closeHotButtons();
    if (onAddStudent) onAddStudent();
    else navigate(ROUTES.STUDENTS);
  };
  const handleAddQR = () => {
    closeHotButtons();
    if (onAddQR) onAddQR();
    else navigate(ROUTES.QR_CODES);
  };

  return (
    <>
      {/* Backdrop - closes sidebar when clicking outside (desktop only) */}
      {isExpanded && onToggle && (
        <div
          className="hidden md:block fixed inset-0 z-[59] bg-black/20"
          onClick={onToggle}
          aria-hidden
        />
      )}

      {/* Backdrop - closes hot buttons flyout when clicking outside */}
      {!isExpanded && hotButtonsOpen && (
        <div
          className="hidden md:block fixed inset-0 z-[59]"
          onClick={closeHotButtons}
          aria-hidden
        />
      )}

      {/* ─── Desktop Sidebar - Full height, overlays header when expanded ─── */}
      <div
        className={`hidden md:block fixed top-0 left-0 bottom-0 bg-[#1a1818] border-r border-white/10 transition-all duration-300 z-[60] overflow-hidden ${
          isExpanded ? "w-64" : "w-20"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header - Toggle centered when collapsed; Logo + text + toggle on right when expanded */}
          {onToggle && (
            <div className="flex items-center h-16 shrink-0 border-b border-white/10 px-3">
              {isExpanded ? (
                <div className="flex items-center justify-between gap-2 w-full">
                  <Link
                    to={ROUTES.DASHBOARD}
                    className="flex items-center gap-2 hover:opacity-80 min-w-0 pl-2"
                  >
                    <img
                      src="/rollops-logo.png"
                      alt="RollOps"
                      className="w-8 h-8 object-contain shrink-0"
                    />
                    <span className="text-xl font-bold text-[#EBFFEE] whitespace-nowrap truncate">
                      Roll<span className="text-[#9B1421]">O</span>ps
                    </span>
                  </Link>
                  <button
                    onClick={onToggle}
                    className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors shrink-0"
                    aria-label="Toggle sidebar"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <button
                    onClick={onToggle}
                    className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
                    aria-label="Toggle sidebar"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Hot Buttons - + Payment, + Student, + QR */}
          <div className="shrink-0 px-3 pt-3 pb-3 border-b border-white/10 min-w-0">
            {isExpanded ? (
              <div className="flex gap-2">
                <button
                  disabled
                  onClick={handleAddPayment}
                  className="flex-1 flex items-center justify-center h-[40px] p-2.5 rounded-lg border border-white/20 text-text-tertiary opacity-60 cursor-not-allowed"
                  title="Add Payment"
                >
                  <DollarSign className="w-5 h-5" />
                </button>
                <button
                  onClick={handleAddStudent}
                  className="flex-1 flex items-center justify-center h-[40px] p-2.5 rounded-lg border border-white/20 text-text-secondary hover:bg-white/5 hover:text-text-primary hover:border-white/30 transition-colors"
                  title="Add Student"
                >
                  <UserPlus className="w-5 h-5" />
                </button>
                <button
                  onClick={handleAddQR}
                  className="flex-1 flex items-center justify-center h-[40px] p-2.5 rounded-lg border border-white/20 text-text-secondary hover:bg-white/5 hover:text-text-primary hover:border-white/30 transition-colors"
                  title="Add QR"
                >
                  <QrCode className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={() => setHotButtonsOpen((prev) => !prev)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                    hotButtonsOpen
                      ? "border-white/30 bg-white/5 text-text-primary"
                      : "border-white/20 text-text-secondary hover:bg-white/5 hover:text-text-primary hover:border-white/30"
                  }`}
                  title="Quick actions (Payment, Student, QR)"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 pt-3 pb-6 px-3 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item, location.pathname);
              const isDisabled = item.disabled;

              const navClasses = `
                w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all relative group overflow-hidden
                ${
                  isDisabled
                    ? "text-text-tertiary opacity-60 cursor-not-allowed"
                    : isActive
                      ? "bg-rollops-primary/20 text-text-primary"
                      : "text-text-secondary hover:bg-white/5 hover:text-text-primary"
                }
              `;

              if (isDisabled) {
                return (
                  <span
                    key={item.id}
                    className={navClasses}
                    title={!isExpanded ? item.label : undefined}
                    aria-disabled="true"
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span
                      className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                        isExpanded ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {item.label}
                    </span>
                  </span>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={navClasses}
                  title={!isExpanded ? item.label : undefined}
                >
                  {/* Active Indicator Bar */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-rollops-primary rounded-r" />
                  )}

                  {/* Icon */}
                  <Icon
                    className={`w-5 h-5 shrink-0 ${isActive ? "text-[#EBFFEE]" : ""}`}
                  />

                  {/* Label */}
                  <span
                    className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                      isExpanded ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Contact Support Button */}
          <div className="px-3 pb-6">
            <button
              onClick={() =>
                window.open("mailto:support@rollops.pro", "_blank")
              }
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all overflow-hidden text-text-secondary hover:bg-white/5 hover:text-text-primary"
              title={!isExpanded ? "Contact Support" : undefined}
            >
              <HelpCircle className="w-5 h-5 shrink-0" />
              <span
                className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                  isExpanded ? "opacity-100" : "opacity-0"
                }`}
              >
                Contact Support
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Hot Buttons Flyout - slides out from sidebar when collapsed and plus is clicked */}
      {!isExpanded && hotButtonsOpen && (
        <div className="hidden md:flex fixed h-[66px] left-20 top-[calc(3rem+0.95rem)] z-[60] gap-2 p-3 rounded-r-lg border border-l-0 border-white/10 bg-[#1a1818] shadow-xl origin-left animate-in slide-in-from-left-4 duration-200">
          <button
            disabled
            onClick={handleAddPayment}
            className="flex items-center justify-center p-2.5 rounded-lg border border-white/20 text-text-tertiary opacity-60 cursor-not-allowed"
            title="Add Payment"
          >
            <DollarSign className="w-5 h-5" />
          </button>
          <button
            onClick={handleAddStudent}
            className="flex items-center justify-center p-2.5 rounded-lg border border-white/20 text-text-secondary hover:bg-white/5 hover:text-text-primary hover:border-white/30 transition-colors"
            title="Add Student"
          >
            <UserPlus className="w-5 h-5" />
          </button>
          <button
            onClick={handleAddQR}
            className="flex items-center justify-center p-2.5 rounded-lg border border-white/20 text-text-secondary hover:bg-white/5 hover:text-text-primary hover:border-white/30 transition-colors"
            title="Add QR"
          >
            <QrCode className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ─── Mobile Bottom Navigation ─── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#1a1818] border-t border-white/10 z-40">
        <div className="flex items-center justify-around px-1 pt-2 pb-[env(safe-area-inset-bottom,8px)]">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item, location.pathname);
            const isDisabled = item.disabled;

            const mobileClasses = `
              flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors relative min-w-0
              ${
                isDisabled
                  ? "text-text-tertiary opacity-60 cursor-not-allowed"
                  : isActive
                    ? "text-[#EBFFEE]"
                    : "text-text-secondary active:text-text-primary"
              }
            `;

            if (isDisabled) {
              return (
                <span
                  key={item.id}
                  className={mobileClasses}
                  aria-disabled="true"
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                    {mobileLabels[item.id] ?? item.label}
                  </span>
                </span>
              );
            }

            return (
              <Link key={item.id} to={item.path} className={mobileClasses}>
                {/* Active top indicator */}
                {isActive && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-rollops-primary rounded-b" />
                )}
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                  {mobileLabels[item.id] ?? item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
